import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { UserProfile } from '../../generated/auth/v1/auth_pb';

interface AuthState {
  // User data
  user: UserProfile | null;

  // Token data
  accessToken: string | null;
  refreshToken: string | null;
  tokenExpiresAt: number | null; // Unix timestamp in milliseconds

  // UI state (not persisted)
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitializing: boolean; // For app startup auth check
  isHydrated: boolean; // Track if store has loaded from localStorage
  error: string | null;

  // Actions
  setTokens: (accessToken: string, refreshToken: string, expiresIn: bigint) => void;
  setUser: (user: UserProfile | null) => void;
  clearAuth: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setInitializing: (initializing: boolean) => void;
  setHydrated: (hydrated: boolean) => void;

  // Computed helpers
  isTokenExpired: () => boolean;
  isTokenExpiringSoon: () => boolean; // < 5 minutes remaining
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      accessToken: null,
      refreshToken: null,
      tokenExpiresAt: null,
      isAuthenticated: false,
      isLoading: false,
      isInitializing: true,
      isHydrated: false,
      error: null,

      // Actions
      setTokens: (accessToken, refreshToken, expiresIn) => {
        const tokenExpiresAt = Date.now() + Number(expiresIn) * 1000;
        set({
          accessToken,
          refreshToken,
          tokenExpiresAt,
          isAuthenticated: true,
          error: null,
        });
      },

      setUser: (user) => {
        set({ user });
      },

      clearAuth: () => {
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          tokenExpiresAt: null,
          isAuthenticated: false,
          error: null,
        });
      },

      setLoading: (loading) => {
        set({ isLoading: loading });
      },

      setError: (error) => {
        set({ error });
      },

      setInitializing: (initializing) => {
        set({ isInitializing: initializing });
      },

      setHydrated: (hydrated) => {
        set({ isHydrated: hydrated });
      },

      // Computed helpers
      isTokenExpired: () => {
        const { tokenExpiresAt } = get();
        if (!tokenExpiresAt) return true;
        return Date.now() >= tokenExpiresAt;
      },

      isTokenExpiringSoon: () => {
        const { tokenExpiresAt } = get();
        if (!tokenExpiresAt) return true;
        // Check if token expires in < 5 minutes
        const fiveMinutes = 5 * 60 * 1000;
        return Date.now() >= tokenExpiresAt - fiveMinutes;
      },
    }),
    {
      name: 'netgrocer-auth',
      // Only persist auth-related state, not UI state
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        tokenExpiresAt: state.tokenExpiresAt,
        isAuthenticated: state.isAuthenticated,
      }),
      // Called when rehydration completes
      onRehydrateStorage: () => (state) => {
        // Set hydrated flag to true after loading from localStorage
        state?.setHydrated(true);
      },
    }
  )
);
